import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Clock,
  Banknote,
  Cpu,
  HeartHandshake,
  SlidersHorizontal,
  Users,
  Stethoscope,
  Activity,
  Heart,
  ClipboardList,
  Phone,
  CheckCircle2,
  MessageCircle,
  FileSignature,
  Laptop,
  ChevronDown,
  ArrowRight,
  Sprout,
  Scale,
  Shield,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

const benefits = [
  {
    icon: Clock,
    title: "Fleksibilitet der passer dig",
    description:
      "Du bestemmer selv hvornår og hvor meget du vil arbejde. Dag, aften, nat — du vælger. Vi tilpasser vagtene efter din hverdag, ikke omvendt.",
  },
  {
    icon: Banknote,
    title: "Ordentlig løn og vilkår",
    description:
      "Alle vikarer får samme høje basisløn — over overenskomsterne — uanset erfaring. Ingen interessekonflikter, fuld gennemsigtighed. Pension og feriepenge som standard.",
  },
  {
    icon: Cpu,
    title: "Intelligent vagtmatch",
    description:
      "Vores system kender dine kompetencer og præferencer. Vi matcher dig kun med vagter der passer til dig — den rigtige type, det rigtige sted, den rigtige tid.",
  },
  {
    icon: HeartHandshake,
    title: "Personlig opbakning",
    description:
      "En fast kontaktperson der kender dig, dine styrker og dine ønsker. Vi er altid klar til at hjælpe — også mellem vagterne.",
  },
  {
    icon: SlidersHorizontal,
    title: "Respekt for dine præferencer",
    description:
      "Foretrækker du plejehjem fremfor hjemmepleje? Har du speciale inden for demens eller palliation? Vi lytter og matcher derefter — automatisk, hver gang.",
  },
  {
    icon: Users,
    title: "Fagligt fællesskab",
    description:
      "Du er en del af et hold — ikke bare et nummer i et stort bureau. Vi kender dig, vi følger op, og vi har din ryg. Du arbejder selvstændigt, men du er aldrig alene.",
  },
];

const roles = [
  {
    icon: Stethoscope,
    title: "Sygeplejersker",
    short: "SPL",
    description:
      "Autoriserede sygeplejersker med erfaring fra den kommunale plejesektor. Plejehjem, hjemmepleje eller bosteder.",
  },
  {
    icon: Activity,
    title: "Social- og sundhedsassistenter",
    short: "SSA",
    description:
      "Uddannede SSA'er med passion for ældreplejen. Kompetencer inden for medicin, sårpleje og daglig omsorg.",
  },
  {
    icon: Heart,
    title: "Social- og sundhedshjælpere",
    short: "SSH",
    description:
      "Engagerede SSH'er der gør en forskel i hverdagen. Stabil og omsorgsfuld pleje af borgerne.",
  },
];

const steps = [
  {
    number: 1,
    title: "Udfyld formular",
    description:
      "Udfyld vores hurtige ansøgningsformular med dine grundlæggende oplysninger.",
    icon: ClipboardList,
  },
  {
    number: 2,
    title: "Vi ringer dig op",
    description:
      "Vi kontakter dig inden for 24 timer for en kort og uformel samtale.",
    icon: Phone,
  },
  {
    number: 3,
    title: "Kompetenceskema",
    description:
      "Udfyld dit kompetenceskema så vi kan matche dig med de rette vagter.",
    icon: CheckCircle2,
  },
  {
    number: 4,
    title: "Jobsamtale",
    description:
      "En uformel samtale hvor vi lærer hinanden bedre at kende.",
    icon: MessageCircle,
  },
  {
    number: 5,
    title: "Kontrakt underskrives",
    description:
      "Vi underskriver kontrakten og gør dig klar til din første vagt.",
    icon: FileSignature,
  },
  {
    number: 6,
    title: "Onboarding",
    description:
      "Introduktion til vores system og din første vagt booket.",
    icon: Laptop,
  },
];

const faqs = [
  {
    q: "Hvad er lønnen som vikar hos Plejesøstrene?",
    a: "Alle vores vikarer får samme høje basisløn — et stykke over overenskomsterne — uanset om du er nyuddannet eller har mange års erfaring. Det gør vi bevidst: vi modtager den samme betaling for en vagt uanset hvem vi sender ud. Ved at give alle samme løn sikrer vi, at det kun er det personlige match med den konkrete vagt der er afgørende — aldrig økonomi. Det sikrer at vi intet vinder ved at sende den billigste vikar ud, men vinder alt ved at sende det bedste match. Ingen interessekonflikter, fuld gennemsigtighed.",
  },
  {
    q: "Kan jeg selv bestemme mine arbejdstider?",
    a: "Ja. Du vælger selv hvornår og hvor meget du vil arbejde. Vi tilpasser os din hverdag, uanset om du ønsker fuldtid, deltid eller enkeltstående vagter.",
  },
  {
    q: "Jeg er nyuddannet — kan jeg også søge?",
    a: "Absolut. Vi værdsætter også nyuddannede med engagement og lærevillighed. Vi starter dig op gradvist — eventuelt med følgevagter sammen med en erfaren vikar — og bygger langsomt op i kompleksitet. Du sættes aldrig i situationer du ikke er klar til.",
  },
  {
    q: "Hvad med pension og feriepenge?",
    a: "Alle vores vikarer er ansat på overenskomstmæssige vilkår. Det betyder pension, feriepenge og alle de rettigheder du har krav på.",
  },
  {
    q: "Hvor hurtigt kan jeg komme i gang?",
    a: "Processen fra ansøgning til første vagt tager typisk under en uge. Vi ringer dig op inden for 24 timer efter din ansøgning.",
  },
  {
    q: "Dækker I hele Danmark?",
    a: "Vi dækker i dag primært Odense, men også hele Fyn, Trekantsområdet og Langeland. Det er vores langsigtede mål at dække mere af landet — og på sigt hele Norden — men vi vokser i det tempo, hvor kvaliteten kan følge med, så vi altid er de allerbedste i de områder, vi er i.",
  },
  {
    q: "Kan jeg anbefale en kollega?",
    a: "Ja! Vi har et henvis-en-kollega-program. For hver ny vikar der starter hos os via din anbefaling, modtager du en bonus. De bedste vikarer kommer ofte via anbefalinger fra folk der allerede er med.",
  },
  {
    q: "Kan jeg risikere at få for mange vagter?",
    a: "Nej. Vores system tildeler automatisk kun én vagt per dag, og vi overvåger arbejdsintensiteten. Du bestemmer selv dit tempo — fra et par vagter om måneden til intensive uger. Vi presser aldrig nogen til at tage mere end de ønsker.",
  },
];

export default function ForVikarerPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      {/* Hero */}
      <section
        className="section relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #e8f9fc 0%, #ffffff 40%, #eef2ff 100%)",
        }}
      >
        <div className="absolute top-0 right-0 w-96 h-96 bg-brand-100 rounded-full -translate-y-1/2 translate-x-1/3 opacity-40 blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent-100 rounded-full translate-y-1/3 -translate-x-1/4 opacity-30 blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 md:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            transition={{ staggerChildren: 0.1 }}
            className="max-w-3xl"
          >
            <motion.div variants={fadeUp} transition={{ duration: 0.5 }}>
              <span className="kicker">Bliv vikar hos Plejesøstrene</span>
            </motion.div>

            <motion.h1
              variants={fadeUp}
              transition={{ duration: 0.5 }}
              className="mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight text-slate-900"
            >
              Fleksible vagter. Ordentlige vilkår.{" "}
              <span className="text-brand-500 subtle-glow">
                Personlig opbakning.
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUp}
              transition={{ duration: 0.5 }}
              className="mt-5 text-slate-500 text-base sm:text-lg max-w-2xl leading-relaxed"
            >
              Hos os er du ikke bare et CPR-nummer i et system. Du er en dygtig
              fagperson, der fortjener respekt, ordentlig løn og et vikarbureau
              der faktisk kender dig.
            </motion.p>

            <motion.div
              variants={fadeUp}
              transition={{ duration: 0.5 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <Link to="/kontakt" className="btn btn-primary btn-lg">
                Ansøg som vikar
                <ArrowRight size={18} />
              </Link>
              <a
                href="tel:+4589801515"
                className="btn btn-secondary btn-lg"
              >
                Ring til os: 89 80 15 15
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Fordele */}
      <section className="section py-10 sm:py-14 md:py-18 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="kicker">Dine fordele</span>
            <h2 className="section-title mt-2">
              Hvorfor vælge Plejesøstrene?
            </h2>
            <p className="section-subtitle">
              Vi har bygget et vikarbureau der sætter fagpersonen først — ikke
              administrationen.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="card-elevated p-6">
                <div className="w-10 h-10 rounded-lg bg-brand-50 flex items-center justify-center mb-4">
                  <b.icon className="text-brand-600" size={20} />
                </div>
                <h3 className="font-semibold text-slate-900 text-base">
                  {b.title}
                </h3>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  {b.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mere end bare vagter */}
      <section className="section py-10 sm:py-14 md:py-18 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <span className="kicker">Mere end bare vagter</span>
            <h2 className="section-title mt-2">
              Et vikarbureau der gør tingene anderledes
            </h2>
            <p className="section-subtitle">
              Vi er 100% selvejende — ingen investorer, ingen kvotepres. Det
              betyder at alle beslutninger træffes ud fra hvad der er bedst for
              dig, institutionerne og borgerne.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-2 gap-6">
            <div className="card p-5 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center shrink-0">
                <Sprout className="text-accent-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">
                  Gradvis opstart
                </h3>
                <p className="mt-1 text-sm text-slate-500 leading-relaxed">
                  Er du ny eller usikker? Vi starter dig op gradvist — eventuelt
                  med følgevagter sammen med en af vores erfarne vikarer, så du
                  får en grundig oplæring. Overskuelige opgaver og tryg
                  opbakning, indtil du selv føler dig klar til mere.
                </p>
              </div>
            </div>

            <div className="card p-5 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center shrink-0">
                <MessageCircle className="text-accent-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">
                  Personlig opfølgning under vagten
                </h3>
                <p className="mt-1 text-sm text-slate-500 leading-relaxed">
                  Under dine første vagter kontakter vi dig midt i vagten for at
                  høre om alt går godt. Det er ikke kontrol — det er omsorg.
                </p>
              </div>
            </div>

            <div className="card p-5 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center shrink-0">
                <Scale className="text-accent-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">
                  Retfærdig vagtfordeling
                </h3>
                <p className="mt-1 text-sm text-slate-500 leading-relaxed">
                  Vores system overvåger fordelingen og prioriterer dig hvis du
                  ikke har haft mange vagter for nylig. Ingen bliver overset
                  eller glemt.
                </p>
              </div>
            </div>

            <div className="card p-5 flex gap-4">
              <div className="w-10 h-10 rounded-lg bg-accent-50 flex items-center justify-center shrink-0">
                <Shield className="text-accent-600" size={20} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900 text-sm">
                  Maks én vagt om dagen
                </h3>
                <p className="mt-1 text-sm text-slate-500 leading-relaxed">
                  Systemet tildeler automatisk kun én vagt per dag. Du er altid
                  udhvilet og kan yde dit bedste — for borgernes skyld og din
                  egen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hvem søger vi */}
      <section className="section py-10 sm:py-14 md:py-18 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="kicker">Hvem søger vi?</span>
            <h2 className="section-title mt-2">
              Vi søger dygtige fagpersoner til plejesektoren
            </h2>
            <p className="section-subtitle mx-auto">
              Med erfaring fra den kommunale plejesektor — plejehjem,
              hjemmepleje eller bosteder.
            </p>
          </div>

          <div className="mt-12 grid sm:grid-cols-3 gap-6">
            {roles.map((r) => (
              <div key={r.title} className="card-elevated p-6 text-center">
                <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center mx-auto mb-4">
                  <r.icon className="text-brand-600" size={24} />
                </div>
                <h3 className="font-semibold text-slate-900 text-base">
                  {r.title}
                </h3>
                <span className="inline-block mt-1 px-2.5 py-0.5 text-xs font-medium text-brand-600 bg-brand-50 rounded-full">
                  {r.short}
                </span>
                <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                  {r.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Processen */}
      <section className="section py-10 sm:py-14 md:py-18 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-12">
            <span className="kicker">Processen</span>
            <h2 className="section-title mt-2">
              Sådan bliver du vikar hos os
            </h2>
            <p className="section-subtitle mx-auto">
              Fra ansøgning til din første vagt — processen er hurtig og
              uformel.
            </p>
          </div>

          {/* Desktop: Horizontal flow */}
          <div className="hidden lg:block">
            <div className="relative">
              <div
                className="absolute top-8 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-200 via-brand-400 to-brand-600"
                style={{ zIndex: 0 }}
              />
              <div
                className="relative grid grid-cols-6 gap-4"
                style={{ zIndex: 1 }}
              >
                {steps.map((step) => {
                  const Icon = step.icon;
                  return (
                    <div
                      key={step.number}
                      className="flex flex-col items-center"
                    >
                      <div className="relative w-16 h-16 rounded-full bg-white border-2 border-brand-500 flex items-center justify-center mb-4 shadow-card">
                        <Icon
                          className="text-brand-600"
                          size={24}
                          strokeWidth={2}
                        />
                        <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-brand-500 text-white text-xs font-bold flex items-center justify-center">
                          {step.number}
                        </span>
                      </div>
                      <h3 className="font-bold text-sm text-slate-900 text-center mb-1">
                        {step.title}
                      </h3>
                      <p className="text-xs text-slate-500 text-center leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Mobile/Tablet: Vertical flow */}
          <div className="lg:hidden space-y-4">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isLast = index === steps.length - 1;
              return (
                <div key={step.number} className="relative">
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center flex-shrink-0">
                      <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-white border-2 border-brand-500 flex items-center justify-center shadow-card">
                        <Icon
                          className="text-brand-600"
                          size={22}
                          strokeWidth={2}
                        />
                        <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-brand-500 text-white text-[10px] font-bold flex items-center justify-center">
                          {step.number}
                        </span>
                      </div>
                      {!isLast && (
                        <div
                          className="w-0.5 flex-1 bg-gradient-to-b from-brand-400 to-brand-200 mt-2 mb-2"
                          style={{ minHeight: "20px" }}
                        />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <h3 className="font-bold text-base sm:text-lg text-slate-900 mb-1">
                        {step.title}
                      </h3>
                      <p className="text-sm text-slate-500 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 sm:mt-12 text-center">
            <Link to="/kontakt" className="btn btn-primary btn-lg">
              Kom i gang nu
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section py-10 sm:py-14 md:py-18 bg-slate-50">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="kicker">Ofte stillede spørgsmål</span>
            <h2 className="section-title mt-2">Det vil du måske gerne vide</h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="card overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <span className="font-semibold text-slate-900 text-sm sm:text-base pr-4">
                    {faq.q}
                  </span>
                  <ChevronDown
                    className={`shrink-0 text-slate-400 transition-transform duration-200 ${
                      openFaq === i ? "rotate-180" : ""
                    }`}
                    size={20}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 text-sm text-slate-500 leading-relaxed">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="section relative overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, #e8f9fc 0%, #ffffff 40%, #eef2ff 100%)",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight">
            Klar til at tage det næste skridt?
          </h2>
          <p className="mt-4 text-slate-500 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Udfyld vores kontaktformular, så ringer vi dig op inden for 24
            timer.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/kontakt" className="btn btn-primary btn-lg">
              Ansøg som vikar
              <ArrowRight size={18} />
            </Link>
          </div>
          <p className="mt-4 text-sm text-slate-400">
            Eller ring direkte:{" "}
            <a
              href="tel:+4589801515"
              className="font-semibold text-brand-600 hover:underline"
            >
              89 80 15 15
            </a>
          </p>
        </div>
      </section>
    </>
  );
}
